import React, { useCallback, useEffect, useState } from "react";
import { Container } from "./Container";
import { getHasuraRoleContext } from "../../utils/functions";
import Loading from "../Loading";
import { Button, Modal, Spinner } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import {
  ApolloClientObject,
  CompanyInformation,
  DatahubType,
  JoinRequestStatus,
  UserRole,
} from "../../types";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import fetchOrganisationQuery, {
  crmOrganisation,
  fetchOrganisationQueryResult,
  fetchOrganisationVariables,
} from "../../graphqlQueries/fetchOrganisation";
import CreateCompanyAccountWizard from "../../pages/SignUpWizard/CreateCompanyAccountWizard";
import { useKeycloak } from "@react-keycloak/web";
import { COMPANY_PATH, DASHBOARD_PATH } from "../../utils/routeConstants";
import getCompanyNamesQuery, { CompaniesResult } from "../../graphqlQueries/getCompanyNames";
import { userInfoVar } from "../../utils/ApolloCache";
import {
  CompanyByBusinessIdResult,
  getCompaniesByBusinessIds,
} from "../../graphqlQueries/getCompanyId";
import joinCompany, {
  JoinCompanyResult,
  JoinCompanyVariables,
  JoinRequestObject,
  JoinRequestResult,
  insertJoinRequests,
} from "../../graphqlQueries/joinCompany";
import getJoinRequests, {
  GetJoinRequestsResponse,
  GetJoinRequestsVariables,
  getJoinRequestsForUser,
} from "../../graphqlQueries/getJoinRequests";
import { BasicModal } from "../DataHubModal";
import processJoinRequestQuery, {
  ProcessJoinRequestResult,
  ProcessJoinRequestVariables,
} from "../../graphqlQueries/processJoinRequest";

export const fetchOrganisation = async (
  businessId: string,
  apolloClient: ApolloClientObject,
  userRole: UserRole
): Promise<fetchOrganisationQueryResult | undefined> => {
  try {
    const result = await apolloClient.query<
      fetchOrganisationQueryResult,
      fetchOrganisationVariables
    >({
      context: getHasuraRoleContext(userRole),
      query: fetchOrganisationQuery,
      variables: {
        businessId: businessId,
      },
    });
    if (result.data?.FetchOrganisation?.status === "ok") {
      return result.data;
    } else {
      console.log("Invalid response status:", result.data?.FetchOrganisation?.status);
      return undefined;
    }
  } catch (e) {
    console.error("Fetch organization error:", e);
    return undefined;
  }
};

type JoinRequestModalProps = {
  onClose: () => void;
  onSubmit: () => void;
  companyName: string;
  isLoading: boolean;
  showConfirmation: boolean;
};

const JoinRequestModal: React.FC<JoinRequestModalProps> = ({
  onClose,
  onSubmit,
  companyName,
  isLoading,
  showConfirmation,
}) => {
  const { t } = useTranslation();

  return (
    <BasicModal
      header={t("signup.companyExistsWizardJoinRequestButton")}
      onCancel={() => (showConfirmation ? null : onClose())}
      onProceed={() => (showConfirmation ? null : onSubmit())}
      proceedText={t("common.join")}
      cancelText={t("common.cancel")}
      centered
    >
      <div className="p-3">
        {isLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">{t("common.loading")}</p>
          </div>
        ) : null}
        {!isLoading && showConfirmation ? (
          <div className="text-center py-4">
            <p className="font-heavy color-primary mb-3">
              {t("signup.companyExistsWizardRequestSentTitle")}
            </p>
            <p>{t("common.redirecting")}</p>
          </div>
        ) : (
          <p className="mb-3">
            {t("signup.companyExistsWizardJoinRequestText")} {companyName}
          </p>
        )}
      </div>
    </BasicModal>
  );
};

export const SelectAccount = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [companies, setCompanies] = useState<crmOrganisation[]>([]);
  const [companyInformation, setCompanyInformation] = useState<CompanyInformation>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCurator, setIsCurator] = useState(false);
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();
  const apolloClient = useApolloClient();
  const userInfo = userInfoVar();
  const userBusinessId = keycloak.tokenParsed?.business_id as string[] | [];
  const [hasCompanyId, setHasCompanyId] = useState(!!keycloak.tokenParsed?.company_ids);
  const [joinRequestLoading, setJoinRequestLoading] = useState(false);
  const [showJoinRequestConfirmation, setShowJoinRequestConfirmation] = useState(false);
  const [showJoinRequestModal, setShowJoinRequestModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<{ id: string; name: string } | null>(null);
  const TIMEOUT_COUNT = 2000;

  const [joinCompanyMutation] = useMutation<JoinCompanyResult, JoinCompanyVariables>(joinCompany, {
    context: getHasuraRoleContext(UserRole.CreateCompany),
  });

  const [sendJoinRequests] = useMutation<JoinRequestResult, { objects: JoinRequestObject[] }>(
    insertJoinRequests
  );

  const [processJoinRequest] = useMutation<ProcessJoinRequestResult, ProcessJoinRequestVariables>(
    processJoinRequestQuery,
    { context: getHasuraRoleContext(UserRole.ManageCompany) }
  );

  const { refetch: refetchJoinRequests } = useQuery<
    GetJoinRequestsResponse,
    GetJoinRequestsVariables
  >(getJoinRequests, {
    context: getHasuraRoleContext(UserRole.CreateCompany),
    variables: {
      userId: userInfo?.userId ?? "",
    },
  });

  const handleJoinRequest = async () => {
    if (!userInfo || !selectedCompany) {
      console.log("Missing userInfo or selectedCompany");
      return;
    }

    try {
      setJoinRequestLoading(true);

      await joinCompanyMutation({
        context: getHasuraRoleContext(UserRole.CreateCompany),
        refetchQueries: [{ query: getJoinRequestsForUser }],
        variables: {
          companyId: selectedCompany.id,
        },
      }).catch((error) => {
        console.log("Error in joinCompanyMutation:", error);
        console.log("Error response:", error.networkError?.result);
        throw error;
      });

      const updatedJoinRequests = await refetchJoinRequests();
      const latestRequest = updatedJoinRequests.data?.companyJoinRequest.find(
        (request) => request.company.id === selectedCompany.id
      );

      if (!latestRequest) {
        // Create a single join request for the selected company only
        await sendJoinRequests({
          context: getHasuraRoleContext(UserRole.CreateCompany),
          refetchQueries: [getJoinRequestsForUser],
          variables: {
            objects: [
              {
                companyId: selectedCompany.id,
                userEmail: userInfo.email as string,
                userId: userInfo.userId,
              },
            ],
          },
        });
      }

      if (latestRequest) {
        const processResult = await processJoinRequest({
          context: getHasuraRoleContext(UserRole.CreateCompany),
          variables: {
            approveRequest: true,
            id: latestRequest.id,
          },
        });

        if (
          processResult.data?.ProcessJoinRequest.joinRequest.status === JoinRequestStatus.Approved
        ) {
          await keycloak.updateToken(-1);
          setJoinRequestLoading(false);
          setShowJoinRequestConfirmation(true);

          setTimeout(() => {
            window.location.href = `${COMPANY_PATH}/${selectedCompany.id}`;
          }, TIMEOUT_COUNT);
        } else {
          throw new Error("Join request was not approved");
        }
      }
    } catch (error) {
      console.error("Error processing join request:", error);
      if (error instanceof Error) {
        if (error.message.includes("BusinessId")) {
          console.error("You don't have access to join this company - business ID mismatch");
        } else {
          console.error("Failed to join company:", error.message);
        }
      }
      setShowJoinRequestModal(false);
      setSelectedCompany(null);
    } finally {
      setJoinRequestLoading(false);
    }
  };

  const companyCheck = useCallback(async () => {
    if (userInfo && userBusinessId) {
      try {
        setLoading(true);
        const businessIds = Array.isArray(userBusinessId) ? userBusinessId : [userBusinessId];

        const fetchPromises = businessIds.map(async (businessId) => {
          const result = await fetchOrganisation(businessId, apolloClient, UserRole.CreateCompany);
          return result?.FetchOrganisation;
        });

        const results = await Promise.all(fetchPromises);
        const validResults = results.filter(
          (result): result is crmOrganisation => result !== undefined
        );

        setCompanies(validResults);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error("Companies not found for user.", error);
      }
    }
  }, [apolloClient, userInfo, userBusinessId]);

  useEffect(() => {
    void companyCheck();
  }, [companyCheck]);

  useEffect(() => {
    setHasCompanyId(!!keycloak.tokenParsed?.company_ids);
  }, [keycloak.tokenParsed?.company_ids, userInfo]);

  const handleOpen = (businessId: string, companyName: string, isCurator: boolean) => {
    if (isCurator) {
      setIsCurator(true);
    } else {
      setIsCurator(false);
    }
    setCompanyInformation({ businessId, companyName });
    setIsModalOpen(true);
  };

  const { data: usersCompanies } = useQuery<CompaniesResult>(getCompanyNamesQuery, {
    context: getHasuraRoleContext(UserRole.ManageCompany),
    fetchPolicy: "cache-first",
    variables: {
      companyIds: keycloak.tokenParsed?.company_ids,
    },
  });

  const baseContext = getHasuraRoleContext(UserRole.CreateCompany);

  const businessIdHeader = Array.isArray(userBusinessId)
    ? userBusinessId.join(",")
    : userBusinessId;

  const { data: businessEntityCompanies } = useQuery<CompanyByBusinessIdResult>(
    getCompaniesByBusinessIds,
    {
      context: {
        ...baseContext,
        headers: {
          ...baseContext.headers,
          "X-Hasura-Business-Id": businessIdHeader,
        },
      },
      fetchPolicy: "cache-first",
      onError: (error) => {
        console.error("GraphQL error:", error);
      },
      skip: !userInfo?.businessId?.length,
      variables: {
        businessIds: userInfo?.businessId ?? [],
      },
    }
  );

  const companyNames = usersCompanies?.company;

  const loginAsUserWithoutOrganisation = async () => {
    window.location.href = `${DASHBOARD_PATH}`;
  };

  const getFilteredCompanies = (businessId: string, unverifiedDmoList: boolean) =>
    businessEntityCompanies?.businessEntity
      ?.find((b) => businessId.includes(b.businessId))
      ?.companies?.filter((company) => {
        const existsInBusinessEntity = businessEntityCompanies?.businessEntity
          .find((b) => businessId.includes(b.businessId))
          ?.companies?.some((c) => c.id === company.id);

        const notAlreadyMember = !companyNames?.some((cn) => cn.id === company.id);
        const needsVerification =
          company.datahubType === DatahubType.DMO &&
          company.companyJoinRequests.every((jr) => jr.status !== JoinRequestStatus.Approved);

        return (
          existsInBusinessEntity && notAlreadyMember && needsVerification === unverifiedDmoList
        );
      });

  return (
    <Container>
      <div>
        <h3 className="text-landing-h4 mb-5 text-center">
          {t("signup.companyExistsWizardHeader")}
        </h3>
        {loading ? (
          <Loading />
        ) : (
          companies
            ?.filter(
              (company, i) =>
                company.businessid !== null && company.businessid === userBusinessId[i]
            )
            .map(({ name }, i) => (
              <React.Fragment key={userBusinessId[i]}>
                <div className="mt-4 border-bottom-gray-300 pb-4">
                  <div className="mt-4 mb-4">
                    <MainCompany
                      title={t("signup.companyExistsWizardBusinessName")}
                      content={name ?? "-"}
                    />
                    <MainCompany
                      title={t("signup.companyExistsWizardBusinessId")}
                      content={userBusinessId[i] ?? "-"}
                    />
                    <MainCompany title={t("companyInfo.marketingName")} content={name ?? "-"} />
                  </div>
                  <div className="mb-4">
                    {companyNames && companyNames?.length > 0 && businessEntityCompanies && (
                      <>
                        <p className="mt-3 mb-3 font-heavy">
                          {t("signup.companyExistsWizardCompaniesJoined")}
                        </p>
                        <div className="mb-3">
                          {/* Filter companyNames to only show companies that belong to the current business ID */}
                          {companyNames
                            .filter((joinedCompany) => {
                              // Find the business entity that contains this company
                              return businessEntityCompanies.businessEntity
                                .find((entity) => entity.businessId === userBusinessId[i])
                                ?.companies.some((company) => company.id === joinedCompany.id);
                            })
                            .map((company, key) => (
                              <Button
                                key={company.businessName + key}
                                className="w-100 mb-2"
                                variant="outline-primary"
                                onClick={() => {
                                  if (!hasCompanyId) {
                                    handleOpen(userBusinessId[i], name, false);
                                  } else {
                                    window.location.href = `${COMPANY_PATH}/${company.id}`;
                                  }
                                }}
                              >
                                {company.businessName}
                              </Button>
                            ))}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="mb-4">
                    {getFilteredCompanies(userBusinessId[i], false) &&
                      // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
                      getFilteredCompanies(userBusinessId[i], false)!.length > 0 && (
                        <>
                          <p className="mt-3 mb-3 font-heavy">
                            {t("signup.companyExistsWizardCompaniesFound")}
                          </p>
                          <div className="mb-3">
                            {userInfo &&
                              getFilteredCompanies(userBusinessId[i], false)?.map((company) => (
                                <Button
                                  key={company.businessName + i}
                                  className="w-100 mb-2"
                                  variant="outline-primary"
                                  onClick={() => {
                                    setSelectedCompany({
                                      id: company.id,
                                      name: company.businessName,
                                    });
                                    setShowJoinRequestModal(true);
                                  }}
                                >
                                  {company.businessName}
                                </Button>
                              ))}
                          </div>
                        </>
                      )}
                  </div>
                  <div className="mb-4">
                    {getFilteredCompanies(userBusinessId[i], true) &&
                      // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
                      getFilteredCompanies(userBusinessId[i], true)!.length > 0 && (
                        <>
                          <p className="mt-3 mb-2 font-heavy">
                            {t("signup.companyExistsWizardRequestAwaitsApprovalTitle")}
                          </p>
                          <div className="mb-3">
                            {userInfo &&
                              getFilteredCompanies(userBusinessId[i], true)?.map((company) => (
                                <p className="mt-1 mb-1" key={company.id}>
                                  {company.businessName}
                                </p>
                              ))}
                          </div>
                        </>
                      )}
                  </div>
                  {showJoinRequestModal && selectedCompany && (
                    <JoinRequestModal
                      onClose={() => {
                        if (!joinRequestLoading && !showJoinRequestConfirmation) {
                          setShowJoinRequestModal(false);
                          setSelectedCompany(null);
                        }
                      }}
                      onSubmit={handleJoinRequest}
                      companyName={selectedCompany.name}
                      isLoading={joinRequestLoading}
                      showConfirmation={showJoinRequestConfirmation}
                    />
                  )}
                  <p className="mb-4 mt-4">{t("signup.companyExistsWizardAddNewCompanyText")}</p>
                  <div className="d-flex flex-column align-items-center">
                    <Button
                      className="w-100 mb-2"
                      variant="outline-secondary"
                      onClick={() => handleOpen(userBusinessId[i], name, false)}
                    >
                      {t("signup.companyExistsWizardAddNewCompanyButton")}
                    </Button>
                    <Button
                      className="w-100 mb-2"
                      variant="outline-secondary"
                      onClick={() => handleOpen(userBusinessId[i], name, true)}
                    >
                      {t("signup.companyExistsWizardAddNewCuratorButton")}
                    </Button>
                  </div>
                </div>
                <Modal
                  show={isModalOpen}
                  size="lg"
                  centered
                  animation={false}
                  onHide={() => setIsModalOpen(false)}
                >
                  <div className="flex m-5">
                    <CreateCompanyAccountWizard
                      companyInformation={companyInformation}
                      isCurator={isCurator}
                    />
                  </div>
                </Modal>
              </React.Fragment>
            ))
        )}
        {userBusinessId?.length === 0 || userBusinessId === undefined || userBusinessId === null ? (
          <div className="mt-4 border-bottom-gray-300 pb-4 text-center">
            <p className="col font-heavy p-0">{t("signup.loginAsApiUser")}</p>
            <Button
              id="apiBtn"
              className="col col-6 mt-3"
              variant="light"
              onClick={loginAsUserWithoutOrganisation}
            >
              {t("signup.loginToDatahub")}
            </Button>
          </div>
        ) : undefined}
        <p className="mt-4 px-3 text-smaller color-gray-800 text-center">
          <Trans i18nKey="signup.wrongBusinessIdDescription">
            if you have registered i18n placeholder{" "}
            <a href="mailto:datahub@visitfinland.com">datahub@visitfinland.com</a>
          </Trans>
        </p>
      </div>
    </Container>
  );
};

const MainCompany = ({ title, content }: { title: string; content: string }) => (
  <div className="d-flex flex-row">
    <span className="col col-4 p-0 mr-3">{title}</span>
    <span className="col col-8 font-heavy p-0">{content}</span>
  </div>
);
